/**
 * Single source of truth for staff role_type / staff_type values
 * shown in the various Staff dropdowns (signup, invite, profile, etc.).
 *
 * Mirrors the backend Staff.ROLE_TYPE_CHOICES + role_mapping.py.
 * When updating, keep this file in sync with both.
 */

// All role_type options grouped by their underlying ClubRole / permission group.
// Each entry has { value, label, group } where `group` is the ClubRole name.
export const STAFF_ROLE_OPTIONS = [
  // ─── CEO group ───
  { value: 'ceo', label: 'CEO', group: 'CEO' },
  { value: 'president', label: 'President', group: 'CEO' },
  { value: 'club_owner', label: 'Club Owner', group: 'CEO' },
  { value: 'chairman', label: 'Chairman', group: 'CEO' },
  { value: 'managing_director', label: 'Managing Director', group: 'CEO' },
  { value: 'general_manager', label: 'General Manager', group: 'CEO' },

  // ─── Sporting Director group ───
  { value: 'sporting_director', label: 'Sporting Director', group: 'Sporting Director' },
  { value: 'director_of_football', label: 'Director of Football', group: 'Sporting Director' },
  { value: 'head_of_football_operations', label: 'Head of Football Operations', group: 'Sporting Director' },
  { value: 'football_director', label: 'Football Director', group: 'Sporting Director' },

  // ─── Technical Director group ───
  { value: 'technical_director', label: 'Technical Director', group: 'Technical Director' },
  { value: 'head_of_football', label: 'Head of Football', group: 'Technical Director' },
  { value: 'director_of_football_methodology', label: 'Director of Football Methodology', group: 'Technical Director' },

  // ─── Club Admin group ───
  { value: 'club_admin', label: 'Club Admin', group: 'Club Admin' },
  { value: 'club_secretary', label: 'Club Secretary', group: 'Club Admin' },
  { value: 'general_secretary', label: 'General Secretary', group: 'Club Admin' },
  { value: 'head_of_administration', label: 'Head of Administration', group: 'Club Admin' },
  { value: 'football_operations_manager', label: 'Football Operations Manager', group: 'Club Admin' },
  { value: 'transfer_manager', label: 'Transfer Manager', group: 'Club Admin' },

  // ─── Head of Scouting group ───
  { value: 'head_of_scouting', label: 'Head of Scouting', group: 'Head of Scouting' },
  { value: 'head_of_recruitment', label: 'Head of Recruitment', group: 'Head of Scouting' },
  { value: 'chief_scout', label: 'Chief Scout', group: 'Head of Scouting' },
  { value: 'head_of_analysis', label: 'Head of Analysis', group: 'Head of Scouting' },
  { value: 'head_of_recruitment_analysis', label: 'Head of Recruitment Analysis', group: 'Head of Scouting' },
  { value: 'head_of_player_identification', label: 'Head of Player Identification', group: 'Head of Scouting' },
  { value: 'head_of_football_analytics', label: 'Head of Football Analytics', group: 'Head of Scouting' },
  { value: 'head_of_data', label: 'Head of Data', group: 'Head of Scouting' },
  { value: 'recruitment_director', label: 'Recruitment Director', group: 'Head of Scouting' },

  // ─── Scout Team group ───
  { value: 'scout', label: 'Scout', group: 'Scout Team' },
  { value: 'youth_scout', label: 'Youth Scout', group: 'Scout Team' },
  { value: 'analyst', label: 'Analyst', group: 'Scout Team' },
  { value: 'performance_analyst', label: 'Performance Analyst', group: 'Scout Team' },
  { value: 'video_analyst', label: 'Video Analyst', group: 'Scout Team' },
  { value: 'data_analyst', label: 'Data Analyst', group: 'Scout Team' },
  { value: 'recruitment_analyst', label: 'Recruitment Analyst', group: 'Scout Team' },
  { value: 'opposition_analyst', label: 'Opposition Analyst', group: 'Scout Team' },

  // ─── Head Coach group ───
  { value: 'head_coach', label: 'Head Coach', group: 'Head Coach' },
  { value: 'manager', label: 'Manager', group: 'Head Coach' },
  { value: 'first_team_coach', label: 'First Team Coach', group: 'Head Coach' },

  // ─── Co Trainer / Assistant Coach group ───
  { value: 'assistant_coach', label: 'Assistant Coach', group: 'Co Trainer / Assistant Coach' },
  { value: 'goalkeeping_coach', label: 'Goalkeeping Coach', group: 'Co Trainer / Assistant Coach' },
  { value: 'strength_and_conditioning_coach', label: 'Strength and Conditioning Coach', group: 'Co Trainer / Assistant Coach' },
  { value: 'youth_coach', label: 'Youth Coach', group: 'Co Trainer / Assistant Coach' },

  // ─── Default Staff Role group ───
  { value: 'team_manager', label: 'Team Manager', group: 'Default Staff Role' },
  { value: 'physiotherapist', label: 'Physiotherapist', group: 'Default Staff Role' },
  { value: 'sports_doctor', label: 'Sports Doctor', group: 'Default Staff Role' },
  { value: 'nutritionist', label: 'Nutritionist', group: 'Default Staff Role' },
  { value: 'sports_psychologist', label: 'Sports Psychologist', group: 'Default Staff Role' },
  { value: 'youth_coordinator', label: 'Youth Coordinator', group: 'Default Staff Role' },
  { value: 'medical_staff', label: 'Medical Staff', group: 'Default Staff Role' },
  { value: 'academy_director', label: 'Academy Director', group: 'Default Staff Role' },
  { value: 'other', label: 'Other', group: 'Default Staff Role' },
];

// Filtered subsets for the various dropdowns. These match the existing
// COACH_POSITIONS / STAFF_POSITIONS shape used in tenderChoices.js so callers
// can swap to these without changing their consuming code.
export const COACH_ROLE_OPTIONS = STAFF_ROLE_OPTIONS.filter(
  (o) => o.group === 'Head Coach' || o.group === 'Co Trainer / Assistant Coach'
);

export const NON_COACH_STAFF_ROLE_OPTIONS = STAFF_ROLE_OPTIONS.filter(
  (o) => o.group !== 'Head Coach' && o.group !== 'Co Trainer / Assistant Coach'
);

// Quick lookup maps (built once at module load).
const _BY_VALUE = Object.fromEntries(STAFF_ROLE_OPTIONS.map((o) => [o.value, o]));

/** Human label for a role_type value. Falls back to the snake_case prettified. */
export const labelForStaffRole = (value) => {
  if (!value) return '';
  const entry = _BY_VALUE[value];
  if (entry) return entry.label;
  return String(value)
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ''))
    .join(' ');
};

/** Group/ClubRole name for a role_type value. */
export const groupForStaffRole = (value) => {
  if (!value) return null;
  return _BY_VALUE[value]?.group || null;
};

/**
 * Reduce a (possibly new) role_type value to its canonical "primary" role_type
 * for that group. Used by UI branches that key off `head_coach`, `sporting_director`,
 * etc. — a new title like 'manager' resolves to 'head_coach' so existing branches
 * keep rendering correctly.
 */
const PRIMARY_FOR_GROUP = {
  'CEO': 'ceo',
  'Sporting Director': 'sporting_director',
  'Technical Director': 'technical_director',
  'Club Admin': 'club_admin',
  'Head of Scouting': 'head_of_scouting',
  'Scout Team': 'scout',
  'Head Coach': 'head_coach',
  'Co Trainer / Assistant Coach': 'assistant_coach',
  'Default Staff Role': 'other',
};

export const primaryRoleType = (value) => {
  if (!value) return value;
  const group = groupForStaffRole(value);
  if (!group) return value;
  return PRIMARY_FOR_GROUP[group] || value;
};

const _COACH_ROLE_VALUES = new Set(COACH_ROLE_OPTIONS.map((o) => o.value));

/**
 * True for every role that actually coaches — the 'Head Coach' group and the
 * 'Co Trainer / Assistant Coach' group, i.e. exactly COACH_ROLE_OPTIONS.
 *
 * Derived from the canonical list above so it can never drift from it: a head
 * coach, a goalkeeping coach and a strength & conditioning coach all have a
 * playing philosophy; a club president, a scout or an accountant does not.
 * A title we do not know yet is reduced to its group first, so a newly added
 * coaching title answers `true` without this file being touched twice.
 */
export const isCoachingRole = (value) => {
  if (!value) return false;
  if (_COACH_ROLE_VALUES.has(value)) return true;
  const primary = primaryRoleType(value);
  return primary === 'head_coach' || primary === 'assistant_coach';
};
